import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogContent, List, ListItemButton, ListItemText, Paper,
    TextField, Typography,
} from '@mui/material';
import { personName } from '../../voice/callFormat';
import { errorMessage } from './missedCallsApi';
import {
    chooseIdentity, clearUploadSession, confirmSession, getUploadAdmin, getUploadToken,
    identityIsStale, onSessionLost, requestAccess, touchActivity,
} from './uploadAccess';

/**
 * The way in to the phone's upload page (server docs/MISSED_CALLS_PLAN.md §6.10): one shared password, then
 * "who is uploading?". No Universe login — whoever holds the office iPhone does not have one.
 *
 * Admins share that phone, so the NAME is re-asked after 30 idle minutes while the password stays remembered.
 * The re-ask is an overlay on top of the running page, never a replacement for it: unmounting would throw away a
 * half-typed review, and the common case is the same person coming back to their own work.
 *
 * Nothing here is a security boundary on its own; the server decides. The gate only keeps the page from showing
 * a form that every request would bounce, and remembers the choice so the phone is not asked every day.
 */
const INPUT_SX = { '& .MuiInputBase-input': { fontSize: 16 } };   // iOS Safari zooms fields under 16px

const Shell = ({ children }) => (
    <Box sx={{
        flex: 1, minWidth: 0, minHeight: '100vh',
        display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
    }}>
        <Paper variant="outlined" sx={{ p: 3, width: '100%', maxWidth: 380 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>부재중 전화 업로드</Typography>
            {children}
        </Paper>
    </Box>
);

const Spinner = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={28} /></Box>
);

const UploadGate = ({ children }) => {
    // 'checking' while a stored token is revalidated, then 'password' → 'pick' → 'ready'.
    const [stage, setStage] = useState(() => (getUploadToken() ? 'checking' : 'password'));
    const [admin, setAdmin] = useState(() => getUploadAdmin());
    const [password, setPassword] = useState('');
    const [pickToken, setPickToken] = useState(null);   // set ONLY by the password step; cleared once used
    const [admins, setAdmins] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [forcePick, setForcePick] = useState(false);  // 변경: ask the name even when nothing has gone stale

    // A stored token may have expired or been invalidated by a password change; find out before trusting it.
    // This also covers the cold start: iOS discards backgrounded Safari tabs, so "reopened the app an hour later"
    // usually arrives as a fresh load rather than a visibility event, and the idle check has to happen here too.
    useEffect(() => {
        let alive = true;
        if (stage !== 'checking') return undefined;
        confirmSession().then((session) => {
            if (!alive) return;
            if (session === null) { setStage('password'); return; }          // definitely signed out
            if (session === undefined) {                                     // could not tell — offline, 500
                // A dead spot must not cost the password, or the office loses the page whenever the signal dips.
                setStage(admin ? 'ready' : 'password');
                return;
            }
            setAdmin(session.admin);
            if (forcePick || identityIsStale()) {
                setAdmins(session.admins);
                setForcePick(false);
                setStage('pick');
                return;
            }
            setStage('ready');
        });
        return () => { alive = false; };
    }, [stage, forcePick, admin]);

    // Left alone long enough that the next person to pick the phone up may not be the last one to use it. Ask the
    // name again — never the password. Three ways in: the tab coming back to the front, the phone sitting
    // untouched with the page open, and the cold start handled by 'checking' above.
    useEffect(() => {
        if (stage !== 'ready') return undefined;
        const reask = () => { if (identityIsStale()) setStage('checking'); };
        const active = () => { touchActivity(); };
        const onVisible = () => { if (document.visibilityState === 'visible') reask(); else touchActivity(); };

        const timer = setInterval(reask, 60 * 1000);
        document.addEventListener('visibilitychange', onVisible);
        ['pointerdown', 'keydown'].forEach((e) => window.addEventListener(e, active, { passive: true }));
        touchActivity();
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
            ['pointerdown', 'keydown'].forEach((e) => window.removeEventListener(e, active));
        };
    }, [stage]);

    // The token can stop working while the page is open (expiry, or the password rotated on the server). Without
    // this the phone would keep showing 읽는 중 for ever while every poll 401s in silence.
    useEffect(() => onSessionLost(() => {
        setAdmin(null);
        setPickToken(null);
        setAdmins([]);
        setError('세션이 만료되었습니다. 비밀번호를 다시 입력해 주세요.');
        setStage('password');
    }), []);

    const submitPassword = async (e) => {
        e.preventDefault();
        if (!password || busy) return;
        setBusy(true);
        setError('');
        try {
            const data = await requestAccess(password);
            setPickToken(data.token);
            setAdmins(data.admins || []);
            setPassword('');
            setStage('pick');
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const pick = async (who) => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            // pickToken exists only straight after the password step and is good for 10 minutes; a re-ask happens
            // at 30, so it would always be dead by then. Clearing it once used — and falling back to the stored
            // token — is what keeps the promise that re-asking the name never asks for the password again.
            const data = await chooseIdentity(pickToken || getUploadToken(), who);
            setPickToken(null);
            setAdmin(data.admin);
            touchActivity();
            setStage('ready');
        } catch (err) {
            setError(errorMessage(err));
            if (err?.response?.status === 401) { setPickToken(null); setStage('password'); }
        } finally {
            setBusy(false);
        }
    };

    /** 변경 — hand the phone over. Re-asks the name, not the password, exactly as the idle rule does. */
    const changeWho = () => {
        setPickToken(null);
        setError('');
        setForcePick(true);
        setStage('checking');
    };

    const backToPassword = () => {
        clearUploadSession();
        setAdmin(null);
        setPickToken(null);
        setAdmins([]);
        setError('');
        setStage('password');
    };

    const picker = (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {pickToken
                    ? '업로드하는 사람을 선택해 주세요.'
                    : '한동안 사용하지 않았습니다. 업로드하는 사람을 다시 선택해 주세요.'}
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <List dense sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {admins.map((a) => (
                    // 48px so the one tap this screen exists for is a comfortable target on a phone; the
                    // default dense row lands at 40, under the 44px minimum.
                    <ListItemButton key={a.uuid} onClick={() => pick(a)} disabled={busy} divider sx={{ minHeight: 48 }}>
                        <ListItemText primaryTypographyProps={{ fontSize: 16 }} primary={personName(a) || a.uuid} />
                    </ListItemButton>
                ))}
            </List>
            {!admins.length && <Alert severity="warning">선택할 수 있는 담당자가 없습니다.</Alert>}
            <Button fullWidth onClick={backToPassword} sx={{ mt: 1 }}>비밀번호 다시 입력</Button>
        </>
    );

    // ── Before there is any session: the whole screen is the gate ────────────────────────────────
    if (!admin) {
        if (stage === 'checking') return <Shell><Spinner /></Shell>;
        if (stage === 'pick') return <Shell>{picker}</Shell>;
        return (
            <Shell>
                <form onSubmit={submitPassword}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        비밀번호를 입력해 주세요.
                    </Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <TextField
                        label="비밀번호"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoFocus
                        fullWidth
                        inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
                        sx={{ ...INPUT_SX, mb: 2 }}
                    />
                    <Button type="submit" variant="contained" fullWidth disabled={!password || busy} sx={{ py: 1.2, fontSize: 16 }}>
                        {busy ? '확인 중…' : '확인'}
                    </Button>
                </form>
            </Shell>
        );
    }

    // ── Signed in: the upload flow, with the re-ask laid OVER it rather than replacing it ────────
    return (
        <Box sx={{
            flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
            height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' },
        }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexShrink: 0,
                px: 2, py: 1, borderBottom: 1, borderColor: 'divider',
            }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                    {personName(admin) || ''} 님으로 업로드
                </Typography>
                <Button size="small" onClick={changeWho} sx={{ flexShrink: 0 }}>변경</Button>
            </Box>
            {/*
              * Keyed on who is uploading: picking a DIFFERENT name remounts the flow, so a half-finished review
              * started by someone else is dropped rather than saved under the new name. The same person re-picking
              * keeps the key — and their work, because the overlay below never unmounts this subtree.
              */}
            <Box key={admin.uuid} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                {children}
            </Box>
            <Dialog open={stage !== 'ready'} fullWidth maxWidth="xs" disableEscapeKeyDown>
                <DialogContent>
                    {stage === 'pick' ? picker : <Spinner />}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default UploadGate;
